import { useCallback, useEffect, useState } from 'react';

/**
 * 编辑区外观设置（仅影响编辑器显示，不写入章节内容）：
 * - background      编辑区背景（跟随主题 / 纸张白 / 米黄 / 护眼绿 / 浅灰）
 * - ruleLines       横向稿纸行线（无 / 实线 / 虚线）
 * - paragraphGap    段落间距（紧凑 / 标准 / 宽松）
 * - paragraphIndent 段落首行缩进 2em（中文小说常用，帮助区分段落边界）
 *
 * 持久化到 localStorage（与 editor-font-size 同一策略，避免加载时闪烁）。
 */

export type EditorBackground = 'default' | 'white' | 'beige' | 'green' | 'slate';
export type EditorRuleLines = 'none' | 'solid' | 'dashed';
export type EditorParagraphGap = 'compact' | 'standard' | 'wide';

export interface EditorAppearance {
  background: EditorBackground;
  ruleLines: EditorRuleLines;
  paragraphGap: EditorParagraphGap;
  paragraphIndent: boolean;
}

const STORAGE_KEY = 'editor-appearance';

export const EDITOR_APPEARANCE_DEFAULTS: EditorAppearance = {
  background: 'default',
  ruleLines: 'none',
  paragraphGap: 'standard',
  paragraphIndent: true,
};

function loadAppearance(): EditorAppearance {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EDITOR_APPEARANCE_DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<EditorAppearance>;
    return {
      background: parsed.background === 'default' || parsed.background === 'white' || parsed.background === 'beige' || parsed.background === 'green' || parsed.background === 'slate'
        ? parsed.background
        : EDITOR_APPEARANCE_DEFAULTS.background,
      ruleLines: parsed.ruleLines === 'solid' || parsed.ruleLines === 'dashed'
        ? parsed.ruleLines
        : EDITOR_APPEARANCE_DEFAULTS.ruleLines,
      paragraphGap: parsed.paragraphGap === 'compact' || parsed.paragraphGap === 'wide'
        ? parsed.paragraphGap
        : EDITOR_APPEARANCE_DEFAULTS.paragraphGap,
      paragraphIndent: typeof parsed.paragraphIndent === 'boolean'
        ? parsed.paragraphIndent
        : EDITOR_APPEARANCE_DEFAULTS.paragraphIndent,
    };
  } catch {
    return EDITOR_APPEARANCE_DEFAULTS;
  }
}

export function useEditorAppearance() {
  const [appearance, setAppearance] = useState<EditorAppearance>(loadAppearance);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(appearance));
    } catch { /* localStorage 不可用时静默降级 */ }
  }, [appearance]);

  const update = useCallback(<K extends keyof EditorAppearance>(key: K, value: EditorAppearance[K]) => {
    setAppearance(prev => (prev[key] === value ? prev : { ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => setAppearance(EDITOR_APPEARANCE_DEFAULTS), []);

  return { appearance, update, reset };
}
